import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppThunkDispatch } from "redux/store";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "./style";
import { getAgentsWithLeads, getAgentById, getUser, getLeadsPageFilters } from "redux/selector";
import { setAgentValue } from "redux/slice/agents";
import Fuse from 'fuse.js';
import * as Icon from "react-feather";
import { Avatar } from "component/shared/avatar";
import { Agent } from "model/agent";
import { Theme } from "@mui/material/styles";

interface Props extends WithStyles<typeof styles> {
	theme: Theme;
}

const Component: React.FC<Props> = ({ classes = {}, theme }) => {
	const dispatch = useAppThunkDispatch();
	
	// Redux selectors
	const agents = useSelector((state: RootState) => 
		getAgentsWithLeads(state).sort((a, b) => (a.name || "").localeCompare(b.name || ""))
	);
	const value = useSelector((state: RootState) => state.agents.value);
	const agent = useSelector((state: RootState) => getAgentById(state, value));
	const loading = useSelector((state: RootState) => state.agents.loading);
	const user = useSelector((state: RootState) => getUser(state));

	// Local state
	const [searchResults, setSearchResults] = useState<Agent[]>([]);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [searchQuery, setSearchQuery] = useState("");

	// Initialize search results when agents change
	useEffect(() => {
		setSearchResults(agents);
	}, [agents]);

	// Memoized values
	const unassigned = useMemo(() => value === -1, [value]);

	// Callback functions
	const handleChange = useCallback((newValue: number) => {
		dispatch(setAgentValue(newValue));
		setAnchorEl(null);
	}, [dispatch]);

	const handleUnassign = useCallback(() => {
		dispatch(setAgentValue(-1));
		setAnchorEl(null);
	}, [dispatch]);

	const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const searchQuery = e.target.value;

		if (!searchQuery) {
			setSearchResults(agents);
			setSearchQuery("");
		} else {
			const fuseOptions = {
				keys: ['name', "email"],
				threshold: 0.3,
			};
			const fuse = new Fuse(agents, fuseOptions);
			const searchResults = fuse.search(searchQuery).map((result) => result.item);
			setSearchResults(searchResults);
			setSearchQuery(searchQuery);
		}
	}, [agents]);

	const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
		if (searchResults.length === 0) {
			setSearchResults(agents);
		}
		setAnchorEl(event.currentTarget);
	}, [agents, searchResults.length]);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const highlightSearchQuery = useCallback((text: string, query: string) => {
		const colorScheme = theme.palette.secondary.main;

		if (!query) {
			return text;
		}
		const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
		return text.replace(regex, `<span style="color: ${colorScheme};">$1</span>`);
	}, [theme.palette.secondary.main]);

	const escapeRegExp = useCallback((string: string) => {
		return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	}, []);

	return (
		<Mui.FormControl className={classes.form}>
			<Mui.Button
				
				onClick={handleClick}
				endIcon={anchorEl ? (<Icon.ChevronUp size={20} className={classes.arrow}/>) : (<Icon.ChevronDown size={20} className={classes.arrow}/>)}
				disabled={loading || !agents}
			>
				<Mui.Grid container wrap="nowrap" alignItems="center" className={classes.nameContainer}>
					{agent ? (
						<Mui.Grid item>
							<Avatar phrase={agent.label || agent.email || ""} src={agent.photoUrl} color={theme.palette.primary.main}/>
						</Mui.Grid>
					) : (
						<Mui.Grid item>
							{unassigned ? <Icon.User className={classes.usersIcon} size={36}/> : <Icon.Users className={classes.usersIcon} size={36}/>}
						</Mui.Grid>
					)}
					<Mui.Grid item>
						<Mui.Grid container direction="column" spacing={1}>
							<Mui.Grid item>
								<Mui.Typography className={classes.viewLabel}>
									{"Viewing as..."}
								</Mui.Typography>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography noWrap className={classes.agentName}>
									{agent ? (agent.name || agent.email) : unassigned ? "Unassigned" : "All Agents"}
								</Mui.Typography>
							</Mui.Grid>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.Button>
			<Mui.Popover
				keepMounted
				anchorEl={anchorEl}
				open={!!anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				className={classes.popover}
			>
				<Mui.TextField 
					id="agent-search"
					onChange={handleSearch}
					placeholder="Search Agents"
					color="secondary"
					InputProps={{
						startAdornment: (
							<Icon.Search className={classes.searchIcon}/>
						),
						style: { color: "darkgray" },
					}}
					fullWidth
					className={classes.textField}
				/>
				<Mui.MenuItem 
					value={0}
					onClick={() => handleChange(0)}
					className={classes.menuItem}
				>
					<Icon.Users className={classes.usersIcon} size={36}/>
					<Mui.Typography className={classes.agentNameItem}>
						All Agents
					</Mui.Typography>
				</Mui.MenuItem>
				{searchResults.map((agent: Agent, i: number) => (
					<Mui.MenuItem 
						key={i}
						value={agent.id} 
						onClick={() => handleChange(agent.id)}
						className={agent.id === value ? classes.selected : classes.menuItem}
					>
						<Avatar phrase={agent.name || agent.email || ""} src={agent.photoUrl} color={theme.palette.primary.main}/>
						<Mui.Typography className={classes.agentNameItem}>
							<Mui.Grid dangerouslySetInnerHTML={{ __html: highlightSearchQuery(agent.name || agent.email || "", searchQuery) }} />
						</Mui.Typography>
					</Mui.MenuItem>
				))}
				<Mui.MenuItem
					onClick={handleUnassign}
					className={classes.menuItem}
				>
					<Icon.User size={36} className={classes.usersIcon}/>
					<Mui.Typography className={classes.agentNameItem}>
						Unassigned
					</Mui.Typography>
				</Mui.MenuItem>
			</Mui.Popover>
		</Mui.FormControl>
	);
};

// tss-react's withStyles does not inject a `theme` prop the way the old JSS bridge's
// `withTheme` HOC did; this component reads `theme.palette` in render, so inject it via useTheme.
const ThemedComponent = (props: WithStyles<typeof styles>) => {
	const theme = Mui.useTheme();
	return <Component {...props} theme={theme} />;
};

export const AgentSelector = withStyles(ThemedComponent, styles);
